/**
 * „Canlı Demo"-Login der Demo-Instanz spdemo.emonse.com (spdemo-Spec
 * Phase 3, 19.07.2026).
 *
 * Gegenstück zu POST /api/demo-login (packages/api/src/routes/demo-login.ts):
 * der Worker mintet server-side die Supabase-Session für den fixen Demo-User,
 * dieser Client etabliert sie über die BESTEHENDEN Prod-Pfade — bewusst kein
 * eigener Cookie-Code:
 *   1. supabase.auth.setSession()  → saubere Client-Session (SIGNED_IN-Event,
 *      Refresh-Timer; Muster aus lib/dev-auto-login.ts, das in Prod-Builds
 *      inert ist — deshalb existiert dieser Demo-Zwilling).
 *   2. writeAccessTokenCookie()    → /api/auth/issue-session mintet das
 *      HMAC-signierte sp-access-token-Cookie (alle Gates unverändert).
 *   3. POST /api/firms/select      → signiertes sp-firm-Cookie via Set-Cookie.
 *      sp-firm ist in Prod-Builds HMAC-signiert — das unsignierte
 *      document.cookie-Muster des Dev-Logins würde am SSR-Bootstrap
 *      (lib/server-fns/bootstrap.ts readFirmIdFromCookieHeader) scheitern.
 *   4. Harte Navigation nach /dashboard, damit der SSR-Loader die frischen
 *      Cookies liest (SPA-Nav genügt nicht — Muster aus dev-auto-login.ts).
 *
 * Sichtbarkeit: NUR im Demo-Build. VITE_DEMO_MODE wird ausschließlich in
 * .github/workflows/demo-deploy.yml gesetzt — in Prod-/Preview-Builds ersetzt
 * Vite import.meta.env.VITE_DEMO_MODE durch undefined, isDemoBuild() wird
 * zu `false` und der Minifier entfernt alle Aufrufer-Zweige (kein Fork,
 * Design-Mirror-Hardcode feedback_demo_mirror_all_ui_changes bleibt erfüllt).
 */
import { createClient } from './supabase/client'
import { writeAccessTokenCookie } from './access-token-cookie'

/** True nur im spdemo-Build (VITE_DEMO_MODE='true' aus demo-deploy.yml). */
export function isDemoBuild(): boolean {
  return import.meta.env.VITE_DEMO_MODE === 'true'
}

type DemoLoginSeed = {
  access_token?: string
  refresh_token?: string
  expires_at?: number
  firmId?: string
}

// Single-flight — Doppelklick auf den Button darf nie zwei parallele
// setSession() feuern (navigator.locks-Contention, s. dev-auto-login.ts).
let inFlight: Promise<{ ok: boolean; pinRequired?: boolean }> | null = null

/**
 * Startet die Demo-Session. Liefert { ok: true } wenn Session + beide
 * SSR-Cookies stehen — der Caller navigiert dann hart nach /dashboard.
 */
export function startDemoSession(pin?: string): Promise<{ ok: boolean; pinRequired?: boolean }> {
  if (inFlight) return inFlight
  inFlight = (async () => {
    try {
      // PIN-Gate (19.07.2026): mit gesetztem DEMO_ACCESS_PIN verlangt der
      // Worker den PIN im Body; 403 code 'pin-required' → demo.tsx blendet
      // das Eingabefeld ein (bzw. meldet falschen PIN).
      const res = await fetch('/api/demo-login', {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pin ? { pin } : {}),
      })
      if (res.status === 403) {
        const body = (await res.json().catch(() => ({}))) as { code?: string }
        if (body.code === 'pin-required') return { ok: false, pinRequired: true }
        return { ok: false }
      }
      if (!res.ok) return { ok: false }
      const seed = (await res.json()) as DemoLoginSeed
      if (!seed.access_token || !seed.refresh_token || !seed.firmId) return { ok: false }

      const supabase = createClient()
      const { data, error } = await supabase.auth.setSession({
        access_token: seed.access_token,
        refresh_token: seed.refresh_token,
      })
      if (error || !data.session) return { ok: false }

      const cookie = await writeAccessTokenCookie(
        data.session.access_token,
        data.session.expires_at,
      )
      if (!cookie.ok) return { ok: false }

      // Signiertes sp-firm-Cookie über den bestehenden Endpoint. Retry-
      // Backoff wie auth/callback.tsx (kalter Worker kann das frische JWT
      // beim allerersten Versuch noch mit 401 abweisen).
      let sel: Response | null = null
      for (const wait of [0, 200, 500, 1000]) {
        if (wait) await new Promise((r) => setTimeout(r, wait))
        sel = await fetch('/api/firms/select', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${data.session.access_token}`,
          },
          body: JSON.stringify({ firmId: seed.firmId }),
        })
        if (sel.ok) break
      }
      if (!sel || !sel.ok) return { ok: false }

      // Localhost-Vorschau (/logindemo, 19.07.2026): der Set-Cookie des
      // Demo-Workers trägt Domain=.emonse.com und wird vom Browser auf
      // localhost VERWORFEN → sp-firm-demo unsigned nachschreiben. Der
      // SSR-Bootstrap akzeptiert unsigned nur im Dev-Bypass (localhost),
      // Prod/spdemo bleiben beim signierten Set-Cookie-Pfad (Muster
      // dev-auto-login.ts writeFirmCookie).
      const host = window.location.hostname
      if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
        document.cookie = `sp-firm-demo=${seed.firmId}; Path=/; Max-Age=2592000; SameSite=Lax`
      }

      return { ok: true }
    } catch (err) {
      console.warn('[demo-login] failed:', err)
      return { ok: false }
    } finally {
      inFlight = null
    }
  })()
  return inFlight
}
